import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, INSTAGRAM, WHATSAPP, PHONE_DISPLAY, ADDRESS, LEGAL } from "../lib/i18n";
import { TREATMENTS } from "../lib/treatments";
import { GLOW_PATH } from "../lib/season";

const SeoLinks = () => {
  const { lang } = useLang();
  const en = lang === "en";
  const a = "text-[#c9bfb2] hover:text-[#C9A66B] transition-colors duration-300";
  return (
    <nav aria-label={en ? "Treatments in Catanzaro" : "Trattamenti a Catanzaro"} className="py-10 border-b border-[#F6F2EC]/15" data-testid="footer-seo-links">
      <p className="overline text-[0.62rem] text-[#C9A66B] mb-5">{en ? "Beauty centre in Catanzaro" : "Centro estetico a Catanzaro"}</p>
      <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
        <li><Link to="/centro-estetico-catanzaro" data-testid="footer-hub-link" className={a}>{en ? "Beauty centre Catanzaro" : "Centro estetico Catanzaro"}</Link></li>
        {Object.keys(TREATMENTS).map((k) => (
          <li key={k}><Link to={`/trattamenti/${k}`} data-testid={`footer-treatment-${k}`} className={a}>{TREATMENTS[k][lang].title} {en ? "in" : "a"} Catanzaro</Link></li>
        ))}
        <li><Link to={GLOW_PATH} data-testid="footer-glow-link" className={a}>{en ? "Gift idea: Glow Package" : "Idea regalo: Pacchetto Glow"}</Link></li>
      </ul>
    </nav>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="bg-[#14110F] text-[#F6F2EC] pt-20 pb-10 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className="grid sm:grid-cols-3 gap-10 pb-16 border-b border-[#F6F2EC]/15">
          <Logo dark className="h-20 sm:h-24" />
          <div className="text-sm text-[#c9bfb2] space-y-2"><p>{ADDRESS}</p><p>WhatsApp · {PHONE_DISPLAY}</p></div>
          <div className="flex sm:justify-end gap-3">
            <a data-testid="footer-instagram-link" href={INSTAGRAM} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full border border-[#F6F2EC]/30 flex items-center justify-center transition-colors duration-300 hover:bg-[#C9A66B] hover:text-[#14110F]"><Instagram className="w-4 h-4" /></a>
            <a data-testid="footer-whatsapp-link" href={WHATSAPP} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-11 h-11 rounded-full border border-[#F6F2EC]/30 flex items-center justify-center transition-colors duration-300 hover:bg-[#C9A66B] hover:text-[#14110F]"><MessageCircle className="w-4 h-4" /></a>
          </div>
        </div>
        <SeoLinks />
        <motion.div
          initial={{ y: "40%", opacity: 0 }}
          whileInView={{ y: "0%", opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="font-stencil font-extralight text-[24vw] leading-[0.85] tracking-[0.08em] text-center text-[#F6F2EC]/90 select-none mt-10"
        >
          GIOIA
        </motion.div>
        <div className="mt-8 pt-6 pb-16 lg:pb-0 lg:pr-20 border-t border-[#F6F2EC]/15 flex flex-col lg:flex-row justify-between gap-4 text-xs text-[#b8ad9f]">
          <div className="space-y-1.5" data-testid="footer-legal">
            <p>© {new Date().getFullYear()} {LEGAL.name} · {t.footer.rights}</p>
            <p>{ADDRESS} · <span data-testid="footer-piva">{t.footer.piva} {LEGAL.piva}</span> · <span data-testid="footer-rea">{t.footer.rea} {LEGAL.rea}</span></p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link to="/shop" data-testid="footer-shop-link" className="underline underline-offset-4 decoration-[#C9A66B]/60 hover:text-[#C9A66B] transition-colors duration-300">Shop</Link>
            <Link to="/quiz-pelle" data-testid="footer-quiz-link" className="underline underline-offset-4 decoration-[#C9A66B]/60 hover:text-[#C9A66B] transition-colors duration-300">Quiz pelle</Link>
            <Link to="/porta-un-amica" data-testid="footer-referral-link" className="underline underline-offset-4 decoration-[#C9A66B]/60 hover:text-[#C9A66B] transition-colors duration-300">Porta un'amica</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="underline underline-offset-4 decoration-[#C9A66B]/60 hover:text-[#C9A66B] transition-colors duration-300">{t.footer.privacy}</Link>
            <p>Catanzaro · Calabria</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const WhatsAppFab = () => (
  <motion.a
    data-testid="floating-whatsapp-btn"
    href={WHATSAPP}
    target="_blank"
    rel="noreferrer"
    aria-label="WhatsApp"
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay: 2, type: "spring", stiffness: 200, damping: 16 }}
    whileHover={{ scale: 1.08 }}
    className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-40 w-14 h-14 rounded-full bg-[#C9A66B] text-[#14110F] flex items-center justify-center shadow-[0_18px_40px_-12px_rgba(20,17,15,0.6)]"
  >
    <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
  </motion.a>
);
